import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { matchStudyToTemplate } from "./dictation-study";
import { publicIdSchema } from "./public-id";

// Dictation templates (radiology dictation mode). A dictation template is a
// private custom template (DoctorTemplate) whose instructions hold a plain
// report with [ ] fields and default normal wording, plus the spoken aliases
// that pick it by voice, and optional plain-language rules for this
// template ("remove the prostate line when the prostate is not dictated").
// They are stored in optionalText behind a first-line marker, so no schema
// change is needed:
//
//   <!-- rosc:dictation {"v":1,"aliases":["normal abdominal"],"rules":"..."} -->
//   ULTRASOUND: ABDOMEN
//   Clinical Indication: [clinical indication]
//   ...
//
// GET /api/v2/templates leaves them out unless asked for (kind=dictation),
// so note template pickers never offer them.

export const DICTATION_TEMPLATE_PREFIX = "<!-- rosc:dictation ";
const MARKER_END = " -->";
export const DICTATION_TEMPLATE_MAX_CHARS = 20000;
export const DICTATION_ALIAS_MAX = 10;
export const DICTATION_ALIAS_MAX_CHARS = 80;
export const DICTATION_RULES_MAX_CHARS = 4000;

export type DictationTemplateContent = {
  aliases: string[];
  body: string;
  // Template-specific rules for the fill, in plain language ("" for none).
  rules: string;
};

const metaSchema = z.object({
  v: z.literal(1),
  aliases: z.array(z.string()).max(DICTATION_ALIAS_MAX).default([]),
  rules: z.string().max(DICTATION_RULES_MAX_CHARS).default(""),
});

const cleanRules = (rules: string | undefined): string =>
  (rules ?? "")
    .replace(/\r\n?/g, "\n")
    .trim()
    .slice(0, DICTATION_RULES_MAX_CHARS);

const cleanAliases = (aliases: readonly string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const alias of aliases) {
    const value = alias
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, DICTATION_ALIAS_MAX_CHARS);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length === DICTATION_ALIAS_MAX) break;
  }
  return out;
};

// Line endings are normalized and trailing blank lines dropped, so the
// stored body matches what the fill endpoint numbers line by line.
export const normalizeDictationBody = (body: string): string =>
  body
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .join("\n")
    .replace(/^\n+|\n+$/g, "");

export const encodeDictationTemplate = ({
  aliases,
  body,
  rules,
}: Omit<DictationTemplateContent, "rules"> & { rules?: string }): string => {
  const cleaned = cleanRules(rules);
  // JSON escapes newlines, and "-->" cannot end the marker early.
  const meta = JSON.stringify({
    v: 1,
    aliases: cleanAliases(aliases),
    ...(cleaned ? { rules: cleaned } : {}),
  }).replace(/-->/g, "--\\u003e");
  return `${DICTATION_TEMPLATE_PREFIX}${meta}${MARKER_END}\n${normalizeDictationBody(body)}`;
};

export const isDictationTemplateText = (
  optionalText: string | null | undefined,
): boolean => !!optionalText?.startsWith(DICTATION_TEMPLATE_PREFIX);

export const parseDictationTemplate = (
  optionalText: string | null | undefined,
): DictationTemplateContent | null => {
  if (!optionalText || !isDictationTemplateText(optionalText)) return null;
  const newline = optionalText.indexOf("\n");
  const header = newline < 0 ? optionalText : optionalText.slice(0, newline);
  if (!header.endsWith(MARKER_END)) return null;
  let meta: z.output<typeof metaSchema>;
  try {
    meta = metaSchema.parse(
      JSON.parse(
        header.slice(
          DICTATION_TEMPLATE_PREFIX.length,
          header.length - MARKER_END.length,
        ),
      ),
    );
  } catch {
    return null;
  }
  return {
    aliases: cleanAliases(meta.aliases),
    body: newline < 0 ? "" : optionalText.slice(newline + 1),
    rules: cleanRules(meta.rules),
  };
};

// The report as it reads with nothing dictated: every [ ] field keeps its
// default text, empty fields disappear, and no brackets are left.
export const renderDictationDefaults = (body: string): string =>
  body
    .split("\n")
    .map((line) =>
      line
        .replace(/\[\s*\]/g, "")
        .replace(/\[([^\]]*)\]/g, "$1")
        .replace(/ {2,}/g, " "),
    )
    .join("\n")
    .trim();

const lineKey = (line: string): string =>
  line.replace(/\s+/g, " ").trim().toLowerCase();

// Report lines (0-based) that differ from the template's default wording:
// the dictated values and changed findings the radiologist should check.
export const changedReportLines = (
  templateBody: string,
  report: string,
): number[] => {
  const defaults = new Set(
    renderDictationDefaults(templateBody)
      .split("\n")
      .map(lineKey)
      .filter(Boolean),
  );
  const changed: number[] = [];
  report.split("\n").forEach((line, index) => {
    const key = lineKey(line);
    if (key && !defaults.has(key)) changed.push(index);
  });
  return changed;
};

// ---------------------------------------------------------------------------
// Voice template pick: find a short spoken command that names a template
// ("Use my normal abdominal template", "Switch to the renal template"),
// fuzzy-match it against the user's template names and aliases, and remove
// the command words from the dictation. Plain code, well under 5 ms.

export type DictationTemplateChoice = {
  id: string;
  name: string;
  aliases?: readonly string[];
  // The report text: when no name fits, a command naming a body part only
  // this template covers ("use my prostate template") picks it.
  body?: string;
};

export type DictationTemplateCommand<T extends DictationTemplateChoice> = {
  template: T | null;
  score: number;
  phrase: string | null;
  // The dictation without the command words.
  body: string;
};

const SYNONYMS: Record<string, string> = {
  ultrasound: "us",
  sonogram: "us",
  sono: "us",
  abdomen: "abdo",
  abdominal: "abdo",
  abdo: "abdo",
  kidney: "renal",
  kidneys: "renal",
  renal: "renal",
  pelvis: "pelvic",
  pelvic: "pelvic",
  ruq: "ruq",
  kub: "kub",
  men: "male",
  man: "male",
  males: "male",
};
const STOP_WORDS = new Set([
  "use",
  "my",
  "the",
  "a",
  "an",
  "please",
  "load",
  "open",
  "start",
  "switch",
  "to",
  "change",
  "apply",
  "pull",
  "up",
  "normal",
  "usual",
  "standard",
  "regular",
  "default",
  "template",
  "templates",
  "for",
  "this",
  "with",
  "go",
  "let's",
  "lets",
  "okay",
  "ok",
  "and",
]);
const tokens = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/right upper quadrant/g, "ruq")
    .replace(/kidneys?,? ureters?,? (and )?bladder/g, "kub")
    .replace(/\bk\.?\s?u\.?\s?b\b/g, "kub")
    .replace(/[^a-z0-9' ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => SYNONYMS[word] ?? word);
const nameTokens = (text: string): Set<string> =>
  new Set(tokens(text).filter((word) => !STOP_WORDS.has(word)));
// A rough sound key, so speech-to-text spellings of the same spoken word
// match: "mail" / "mill" / "male" -> "ml", "cub" / "cube" / "kub" -> "kb".
const soundKey = (word: string): string => {
  const w = word
    .replace(/[^a-z]/g, "")
    .replace(/ph/g, "f")
    .replace(/ck/g, "k")
    .replace(/c(?=[eiy])/g, "s")
    .replace(/[cq]/g, "k")
    .replace(/x/g, "ks")
    .replace(/z/g, "s");
  const rest = w.slice(1).replace(/[aeiouywh]/g, "");
  return (w.slice(0, 1) + rest).replace(/(.)\1+/g, "$1");
};
const sameWord = (a: string, b: string): boolean =>
  a === b ||
  (a.length >= 3 &&
    b.length >= 3 &&
    soundKey(a).length >= 2 &&
    soundKey(a) === soundKey(b));
// Words run together by speech-to-text ("mailcub") are split when both
// halves sound like words of the user's template names.
const splitRunTogether = (
  query: Set<string>,
  known: readonly string[],
): Set<string> => {
  const out = new Set<string>();
  for (const word of query) {
    if (known.some((name) => sameWord(word, name)) || word.length < 6) {
      out.add(word);
      continue;
    }
    let split: [string, string] | null = null;
    for (let at = 3; at <= word.length - 3 && !split; at++) {
      const [head, tail] = [word.slice(0, at), word.slice(at)];
      if (
        known.some((name) => sameWord(head, name)) &&
        known.some((name) => sameWord(tail, name))
      )
        split = [head, tail];
    }
    if (split) for (const part of split) out.add(part);
    else out.add(word);
  }
  return out;
};
const dice = (a: Set<string>, b: Set<string>): number => {
  let shared = 0;
  for (const word of a)
    if ([...b].some((name) => sameWord(word, name))) shared++;
  shared = Math.min(shared, b.size);
  return a.size + b.size ? (2 * shared) / (a.size + b.size) : 0;
};

// A command is a short clause (at most 12 words, at the start or after a
// sentence end) that ends with "template", optionally led by use / load /
// switch to / change to ... and "my" / "the".
const COMMAND =
  /(?:^|[.!?]\s+)((?:(?:ok(?:ay)?|so|now)[,\s]+)?(?:(?:please\s+)?(?:use|load|open|start|apply|switch\s+to|change\s+to|go\s+to|pull\s+up)\s+)?(?:my\s+|the\s+)?(?:[\w-]+[\s,]+){0,7}?templates?)\s*[:.,;-]?\s*/gi;
export const DICTATION_TEMPLATE_MATCH_THRESHOLD = 0.5;
// A spoken body part matched against the templates' own report lines.
const BODY_MATCH_THRESHOLD = 0.55;

export function detectDictationTemplate<T extends DictationTemplateChoice>(
  transcript: string,
  templates: readonly T[],
  threshold = DICTATION_TEMPLATE_MATCH_THRESHOLD,
): DictationTemplateCommand<T> {
  const names = templates.map((template) => ({
    template,
    keys: [template.name, ...(template.aliases ?? [])].map(nameTokens),
  }));
  const known = [
    ...new Set(names.flatMap(({ keys }) => keys.flatMap((key) => [...key]))),
  ];
  for (const match of transcript.matchAll(COMMAND)) {
    const phrase = match[1] ?? "";
    if (phrase.split(/\s+/).length > 12) continue;
    const query = splitRunTogether(nameTokens(phrase), known);
    if (!query.size) continue;
    let best: { template: T; score: number } | null = null;
    for (const { template, keys } of names) {
      const score = Math.max(...keys.map((key) => dice(query, key)));
      if (!best || score > best.score) best = { template, score };
    }
    if (!best || best.score < threshold) {
      // No name fits: a body part that only one template covers.
      const byBody = matchStudyToTemplate(
        [...query].join(" "),
        templates.filter((template) => template.body),
        BODY_MATCH_THRESHOLD,
      );
      if (!byBody) continue;
      best = byBody;
    }
    const leading = match[0].length - match[0].trimStart().length;
    const sentenceEnd = match[0].trimStart().match(/^[.!?]\s+/)?.[0].length;
    const start = (match.index ?? 0) + leading + (sentenceEnd ?? 0);
    const end = (match.index ?? 0) + match[0].length;
    const body = (transcript.slice(0, start) + transcript.slice(end))
      .replace(/^\s*[,.;:]\s*/, "")
      .trim();
    return { template: best.template, score: best.score, phrase, body };
  }
  return { template: null, score: 0, phrase: null, body: transcript.trim() };
}

// ---------------------------------------------------------------------------
// POST /api/v2/dictation-reports: fill a dictation template (or the current
// report, when continuing) from the dictated text.

export const dictationReportInputSchema = z.strictObject({
  // The template body (with [ ] fields), or the current report when
  // continuing.
  template: z.string().trim().min(1).max(DICTATION_TEMPLATE_MAX_CHARS),
  // What was dictated, without the template command.
  dictation: z.string().trim().min(1).max(DICTATION_TEMPLATE_MAX_CHARS),
  // The template is the report so far; the dictation adds to or corrects it.
  continuing: z.boolean().default(false),
  // The template's own rules (plain language), applied by the fill.
  rules: z.string().trim().max(DICTATION_RULES_MAX_CHARS).optional(),
});
export type DictationReportInput = z.output<typeof dictationReportInputSchema>;

export const dictationReportGuardSchema = z.object({
  pass: z.boolean(),
  // Dictated numbers that are not in the report.
  missing: z.array(z.number()),
  // Numbers in the report that were neither dictated nor in the template.
  unexpected: z.array(z.number()),
  // Values written more often than they were dictated.
  duplicated: z.array(z.number()),
  // Brackets or markdown left in the report.
  format: z.boolean(),
  // Values the fill wrote that nobody said (computed or inferred, e.g. a
  // retention percentage): removed, the field left as the template's
  // default. Older API builds leave it out.
  notStated: z.array(z.number()).default([]),
});
export type DictationReportGuard = z.output<typeof dictationReportGuardSchema>;

export const dictationReportSchema = z.object({
  report: z.string(),
  // Lines (0-based) this fill changed or added.
  changedLines: z.array(z.number().int().nonnegative()),
  guard: dictationReportGuardSchema,
  // "fallback" when the primary model timed out or failed.
  route: z.enum(["primary", "fallback"]),
  model: z.string(),
  durationMilliseconds: z.number().nonnegative(),
});
export type DictationReport = z.output<typeof dictationReportSchema>;

export const dictationReportContract = {
  createDictationReport: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Not Found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Dictation report unavailable" },
    })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: dictationReportInputSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/dictation-reports",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(dictationReportSchema),
};

// ---------------------------------------------------------------------------
// Saved dictated reports. A report is kept as a visit (the `file` table, so
// it follows the visit list, retention and delete): status
// DICTATION_REPORT_STATUS marks it, `result` holds the report, `transcription`
// the dictated text (segments separated by a blank line) and
// `customTemplate` the dictation template. No schema change.

export const DICTATION_REPORT_STATUS = "Dictated report";
export const DICTATED_REPORT_MAX_BYTES = 60000;
export const isDictatedReport = (status: string | null | undefined): boolean =>
  status === DICTATION_REPORT_STATUS;

const utf8Within = (value: string) =>
  new TextEncoder().encode(value).length <= DICTATED_REPORT_MAX_BYTES;

export const dictatedReportSaveSchema = z.strictObject({
  title: z
    .string()
    .trim()
    .min(1)
    .refine((value) => Array.from(value).length <= 128, "Title too long"),
  report: z
    .string()
    .refine((value) => value.trim().length > 0, "Report is required")
    .refine(utf8Within, "Report is too long"),
  // Dictated segments (without template commands), oldest first.
  dictations: z
    .array(z.string().max(DICTATION_TEMPLATE_MAX_CHARS))
    .max(200)
    .refine((value) => utf8Within(value.join("\n\n")), "Dictation too long"),
  // The dictation template the report was filled from.
  templatePublicId: publicIdSchema.nullable(),
});
export type DictatedReportSave = z.output<typeof dictatedReportSaveSchema>;

// Dictated segments as stored in the visit's transcription column.
export const joinDictations = (dictations: readonly string[]): string =>
  dictations
    .map((value) => value.replace(/\n{2,}/g, "\n").trim())
    .filter(Boolean)
    .join("\n\n");
export const splitDictations = (stored: string | null | undefined): string[] =>
  (stored ?? "")
    .split(/\n{2,}/)
    .map((value) => value.trim())
    .filter(Boolean);

const dictatedReportResultSchema = z.object({ public_id: publicIdSchema });
const savedErrors = {
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Report or template not found" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Dictation report unavailable" },
};
export const dictatedReportContract = {
  createDictatedReport: oc
    .errors(savedErrors)
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: dictatedReportSaveSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/dictated-reports",
        inputStructure: "detailed",
        successStatus: 201,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(dictatedReportResultSchema),
  updateDictatedReport: oc
    .errors(savedErrors)
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: dictatedReportSaveSchema,
      }),
    )
    .meta(
      openapi({
        method: "PUT",
        path: "/api/v2/dictated-reports/{public_id}",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(dictatedReportResultSchema),
};
